import type { DiscoveryChatMessage, DiscoveryNote } from "../types/discoveryChat";

interface DiscoveryChatResponse {
  ok?: boolean;
  reply?: DiscoveryChatMessage;
  history?: DiscoveryChatMessage[];
  error?: string;
}

interface DiscoveryNotesResponse {
  ok?: boolean;
  notes?: DiscoveryNote[];
  note?: DiscoveryNote;
  error?: string;
}

export async function sendDiscoveryChatMessage(
  projectId: string,
  message: string,
  history?: DiscoveryChatMessage[],
): Promise<{ reply: DiscoveryChatMessage; history: DiscoveryChatMessage[] }> {
  if (!projectId) {
    throw new Error("projectId is required");
  }

  const trimmed = message.trim();

  if (!trimmed) {
    throw new Error("message is required");
  }

  const response = await fetch(`/api/projects/${projectId}/discovery/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ message: trimmed, history }),
  });

  const payload = (await response.json()) as DiscoveryChatResponse;

  if (!response.ok || payload.ok === false || !payload.reply || !payload.history) {
    const messageText = payload?.error ?? `Failed to contact discovery assistant (status ${response.status})`;
    throw new Error(messageText);
  }

  return { reply: payload.reply, history: payload.history };
}

export async function fetchDiscoveryNotes(projectId: string): Promise<DiscoveryNote[]> {
  if (!projectId) {
    throw new Error("projectId is required");
  }

  const response = await fetch(`/api/projects/${projectId}/discovery/notes`);
  const payload = (await response.json()) as DiscoveryNotesResponse;

  if (!response.ok || payload.ok === false) {
    const messageText = payload?.error ?? `Failed to load discovery notes (status ${response.status})`;
    throw new Error(messageText);
  }

  return payload.notes ?? [];
}

export async function promoteDiscoveryMessageToNote(
  projectId: string,
  messageText: string,
  messageId?: string,
): Promise<DiscoveryNote> {
  if (!projectId) {
    throw new Error("projectId is required");
  }

  const trimmed = messageText.trim();

  if (!trimmed) {
    throw new Error("message content is required");
  }

  const response = await fetch(`/api/projects/${projectId}/discovery/notes`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ messageText: trimmed, messageId }),
  });

  const payload = (await response.json()) as DiscoveryNotesResponse;

  if (!response.ok || payload.ok === false || !payload.note) {
    throw new Error(payload?.error ?? `Failed to promote message (status ${response.status})`);
  }

  return payload.note;
}
